import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AdminUser } from '../types';
import { AdminLayout } from '../components/Layout';

interface AccessCodeItem {
    id: string;
    name: string;
    client_name: string;
    is_active: number;
    access_code: string;
    role: string;
    created_at: string;
}

export function AdminAccessCodes() {
    const [admin, setAdmin] = useState<AdminUser | null>(null);
    const [accessCodes, setAccessCodes] = useState<AccessCodeItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [copiedCode, setCopiedCode] = useState<string | null>(null);
    const navigate = useNavigate();

    const getApiBase = () => {
        return '/api';
    };

    const request = async (endpoint: string, options: RequestInit = {}) => {
        const response = await fetch(`${getApiBase()}${endpoint}`, {
            ...options,
            credentials: 'include',
            headers: { 'Content-Type': 'application/json', ...options.headers },
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Request failed');
        return data;
    };

    useEffect(() => {
        const stored = localStorage.getItem('rewagent_admin');
        if (stored) setAdmin(JSON.parse(stored));
        else navigate('/admin');
    }, [navigate]);

    useEffect(() => {
        if (admin) fetchAccessCodes();
    }, [admin]);

    const fetchAccessCodes = async () => {
        try {
            const data = await request('/admin/members/all');
            setAccessCodes(data);
        } catch {
        } finally {
            setLoading(false);
        }
    };

    const handleCopyCode = (code: string) => {
        navigator.clipboard.writeText(code);
        setCopiedCode(code);
        setTimeout(() => setCopiedCode(null), 2000);
    };

    const handleLogout = async () => {
        try { await request('/auth/logout', { method: 'POST' }); } catch { }
        localStorage.removeItem('rewagent_admin');
        localStorage.removeItem('rewagent_token');
        navigate('/admin');
    };

    if (!admin) return null;

    return (
        <AdminLayout
            activeTab="access-codes"
            onTabChange={(tab) => {
                const baseTab = tab.split('?')[0];
                navigate(`/admin/${['leads', 'access-codes', 'campaigns', 'logs'].includes(baseTab) ? tab : 'dashboard'}`);
            }}
            onLogout={handleLogout}
        >
            <div className="p-10 max-w-7xl mx-auto">
                <div className="mb-10 flex justify-between items-end">
                    <div>
                        <h1 className="text-3xl font-bold text-[#1a1a1a] mb-2 tracking-tight">Access Codes</h1>
                        <p className="text-[#6b635a] text-sm uppercase tracking-widest font-semibold">View all team member access codes</p>
                    </div>
                </div>

                <div className="bg-[#fcfbfa] border border-[#e3dbcf] rounded-2xl p-8 shadow-sm">
                    <div className="space-y-4">
                        {loading && <div className="text-sm font-bold tracking-widest uppercase text-[#8c8578]">Loading access codes...</div>}
                        {!loading && accessCodes.length === 0 && <div className="text-sm font-bold tracking-widest uppercase text-[#8c8578]">No access codes found.</div>}
                        {accessCodes.map(member => (
                            <div key={member.id} className="flex justify-between items-center p-6 bg-[#f3ede3] border border-[#e3dbcf] rounded-xl hover:border-[#1a1a1a] hover:shadow-md transition-all duration-300">
                                <div>
                                    <div className="flex items-center gap-3">
                                        <span className="font-bold text-[#1a1a1a] text-lg uppercase tracking-tight">{member.name}</span>
                                        <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${member.role === 'qa' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-[#fcfbfa] text-[#1a1a1a] border-[#e3dbcf]'}`}>{member.role === 'admin' || !member.role ? 'owner' : member.role}</span>
                                    </div>
                                    <div className="text-xs text-[#8c8578] mt-2 font-bold tracking-[0.2em] uppercase">
                                        <span className="text-[#1a1a1a]">{member.client_name}</span> / Added {new Date(member.created_at).toLocaleDateString()}
                                    </div>
                                </div>
                                <div className="flex items-center gap-4">
                                    <code className="text-lg font-mono text-[#1a1a1a] font-bold tracking-[0.2em] bg-[#fcfbfa] px-6 py-3 rounded-lg border border-[#e3dbcf]">{member.access_code}</code>
                                    <button onClick={() => handleCopyCode(member.access_code)} className="p-3 text-[#8c8578] hover:text-[#1a1a1a] bg-[#fcfbfa] hover:bg-[#ffffff] border border-[#e3dbcf] hover:border-[#1a1a1a] rounded-lg transition-all active:scale-95 shadow-sm" title="Copy code">
                                        {copiedCode === member.access_code ? (
                                            <svg className="w-5 h-5 text-[#1a1a1a]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                                        ) : (
                                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" /></svg>
                                        )}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
}
